import React from "react";
import type { Dayjs } from "dayjs";
import { Button, DatePicker, Popover, Radio } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { formatISODateToYYYYMMDD } from "@/utils/dateUtils";
import { useCalendarFilter } from "@/hooks/useCalendarFilter";

const { RangePicker } = DatePicker;

type CalendarFilterProps = {
  calendarFilter: ReturnType<typeof useCalendarFilter>;
};

export default function CalendarFilter({
  calendarFilter,
}: CalendarFilterProps) {
  const {
    selectedDates,
    value,
    appliedDates,
    headerText,
    open,
    handleClickTile,
    onChangeRadioButton,
    onChangeDates,
    onClose,
    onClear,
    onApply,
  } = calendarFilter;
  const fontWeightDefault = 400;
  const fontWeightSelected = 600;

  const content = (
    <div className="w-[309px]">
      <div className="flex flex-col w-full gap-2 flex-1">
        <Radio.Group
          value={value}
          options={[
            { value: "on", label: "On" },
            { value: "before", label: "Before" },
            { value: "after", label: "After" },
            { value: "between", label: "Between" },
          ]}
          onChange={onChangeRadioButton}
        />

        {value !== "between" && (
          <div className="flex flex-col gap-2">
            <p className="mt-1">Enter a date</p>
            <DatePicker
              data-testid="notification-date-single-input"
              value={Array.isArray(selectedDates) ? null : selectedDates}
              onChange={onChangeDates}
              style={{ width: "100%" }}
            />
          </div>
        )}

        {value === "between" && (
          <div className="flex flex-col gap-2">
            <p className="mt-2">Enter date range</p>
            <RangePicker
              data-testid="notification-date-range-input"
              value={Array.isArray(selectedDates) ? selectedDates : null}
              onChange={(dates) =>
                onChangeDates(dates as [Dayjs, Dayjs] | null)
              }
              style={{ width: "100%" }}
            />
          </div>
        )}
      </div>
      <Footer onClose={onClose} onApply={onApply} onClear={onClear} />
    </div>
  );

  const getBackgroundColor = () => {
    if (appliedDates != null) {
      return "#ECFAFF";
    }
    return "#FFF";
  };

  const getBorderColor = () => {
    if (appliedDates != null) {
      return "#64BDE1";
    }
    return "#0000001f";
  };

  const getDateText = () => {
    if (!appliedDates) return "";

    return Array.isArray(appliedDates)
      ? `${formatISODateToYYYYMMDD(
          appliedDates[0].toISOString(),
        )} - ${formatISODateToYYYYMMDD(appliedDates[1].toISOString())}`
      : formatISODateToYYYYMMDD(appliedDates.toISOString());
  };

  return (
    <div>
      <Popover
        content={content}
        trigger="click"
        className="w-80"
        styles={{
          root: {
            borderRadius: "8px",
            border: "1px solid #0000001f",
            boxShadow: "none",
          },
        }}
        arrow={false}
        placement="bottomLeft"
        open={open}
        zIndex={1070}
      >
        <div
          className="flex h-9 px-2 py-2 w-[260px] gap-2 items-center justify-between text-[#2B4752] rounded-lg cursor-pointer"
          data-testid="notification-date-filter-toggle"
          style={{
            border: `1px solid ${getBorderColor()}`,
            background: getBackgroundColor(),
          }}
          onClick={handleClickTile}
        >
          {appliedDates === null && (
            <div style={{ fontWeight: fontWeightDefault }}>{headerText}</div>
          )}
          {appliedDates !== null && (
            <AppliedDateDisplay
              headerText={headerText}
              dateText={getDateText()}
              fontWeightSelected={fontWeightSelected}
            />
          )}
          <RightOutlined style={{ color: "#00000073", fontSize: "12px" }} />
        </div>
      </Popover>
    </div>
  );
}

const AppliedDateDisplay = ({
  headerText,
  dateText,
  fontWeightSelected,
}: {
  headerText: string;
  dateText: string;
  fontWeightSelected: number;
}) => (
  <div className="flex items-center justify-between w-full">
    <span style={{ fontWeight: fontWeightSelected }}>{headerText}</span>
    <span
      className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full shrink-0"
      style={{ fontWeight: fontWeightSelected }}
    >
      {dateText}
    </span>
  </div>
);

const Footer = ({
  onClose,
  onClear,
  onApply,
  isApplyDisabled = false,
}: {
  onClose: () => void;
  onClear: () => void;
  onApply: () => void;
  isApplyDisabled?: boolean;
}) => {
  return (
    <div className={"flex justify-between border-t pt-2 mt-4"}>
      <Button
        type="text"
        size="small"
        onClick={onClose}
        className="text-[#064C68]"
      >
        Close
      </Button>

      <div className="flex gap-2">
        <Button size="small" onClick={onClear} className="text-[#2B4752]">
          Clear
        </Button>
        <Button
          size="small"
          className="bg-[#D2EFF9] border border-[#064C68] text-[#064C68]"
          onClick={onApply}
          disabled={isApplyDisabled}
        >
          Apply
        </Button>
      </div>
    </div>
  );
};
